import { formatMoney } from '@/utils/NumericMutations'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import messages from '@/lang'
import i18n from '@/i18n'
import {
  CorrectionType,
  IntegrationSourceType,
  AccountingCategory,
  IntegrationStatus
} from '@/utils/Enumerations.js'
import { DataTableColumn, ColumnSet } from '@/model/DataTableColumn.js'
import { SETTING_KEYS } from '@/utils/UserSettings.js'
import { SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'

const settlementAdvanceHiddenColumnKeys = {
  [SettlementSubKeys.AP.MiscPayments]: SETTING_KEYS.MISC_PAYMENTS_ADVANCES_HIDDEN_COLUMNS
}
const settlementTractPaymentHiddenColumnKeys = {
  [SettlementSubKeys.AP.MiscPayments]: SETTING_KEYS.MISC_PAYMENTS_TRACT_PAYMENTS_HIDDEN_COLUMNS
}
const settlementAccountPaymentHiddenColumnKeys = {
  [SettlementSubKeys.AP.MiscPayments]: SETTING_KEYS.MISC_PAYMENTS_ACCOUNT_PAYMENTS_HIDDEN_COLUMNS
}

const valueOrNotAvailable = (value) => {
  if (value === '' || value === null || value === undefined) {
    return messages['en-US'].notAvailable
  }

  return value
}

export function settlementTractPaymentHeaders (settlementContext = undefined) {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('account'),
      value: 'accountName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('code'),
      value: 'tractCode',
      align: 'center',
      format: valueOrNotAvailable,
      columnAdjusterText: i18n.t('tractCode')
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('activity'),
      value: 'activity',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode',
      align: 'center'
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('exportCode'),
      value: 'exportCode',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('businessEntity'),
      value: 'businessEntityName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      format: formatMoney,
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'settlementTractPaymentHeaders',
    userSettingKey: settlementTractPaymentHiddenColumnKeys[settlementContext]
  })
}

export function settlementAccountPaymentHeaders (settlementContext = undefined) {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('account'),
      value: 'accountName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('code'),
      value: 'accountCode',
      align: 'center',
      format: valueOrNotAvailable,
      columnAdjusterText: i18n.t('accountCode')
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('activity'),
      value: 'activity',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode',
      align: 'center'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('exportCode'),
      value: 'exportCode',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('businessEntity'),
      value: 'businessEntityName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      format: formatMoney,
      hidable: false
    }),
    new DataTableColumn({
      id: 9,
      sortable: false,
      text: i18n.t('actions'),
      value: 'actions',
      align: 'center',
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'settlementAccountPaymentHeaders',
    userSettingKey: settlementAccountPaymentHiddenColumnKeys[settlementContext]
  })
}

export function ticketCorrectionHeaders () {
  return [
    {
      sortable: true,
      text: i18n.t('ticketNumber'),
      value: 'ticketNumber'
    },
    {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tract'
    },
    {
      sortable: true,
      text: i18n.t('account'),
      value: 'account'
    },
    {
      sortable: true,
      text: i18n.t('destination'),
      value: 'destination'
    },
    {
      sortable: true,
      text: i18n.t('fromAccount'),
      value: 'fromAccount'
    },
    {
      sortable: true,
      text: i18n.t('setting'),
      value: 'setting'
    },
    {
      sortable: true,
      text: i18n.t('type'),
      value: 'correctionType',
      format: (value) => CorrectionType.forInt(value).name
    },
    {
      text: i18n.t('notes'),
      value: 'notes'
    },
    {
      sortable: true,
      text: i18n.t('createdBy'),
      value: 'createdBy'
    },
    {
      sortable: true,
      text: i18n.t('createdAt'),
      value: 'createdAt',
      format: (value) => utcToLocalDate(value, 'L - LT')
    },
    {
      align: 'center',
      text: i18n.t('actions'),
      value: 'actions'
    }
  ]
}

export function settlementAdvanceHeaders (isExportBatch, settlementContext = undefined) {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('payee'),
      value: 'payeeName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('entity'),
      value: 'businessEntityName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('tract'),
      value: 'tract',
      align: 'left',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode',
      align: 'center'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('exportCode'),
      value: 'exportCode',
      align: 'center',
      format: valueOrNotAvailable
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: isExportBatch ? i18n.t('recoveredInBatch') : i18n.t('recoveries'),
      value: 'recovered',
      align: 'right',
      format: formatMoney,
      hidable: false
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      format: formatMoney,
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'settlementAdvanceHeaders',
    userSettingKey: settlementAdvanceHiddenColumnKeys[settlementContext]
  })
}

export function businessIntegrationHeaders (category) {
  let headers = [
    {
      sortable: true,
      text: i18n.t('entityCode'),
      value: 'businessEntityCode',
      align: 'center'
    },
    {
      sortable: true,
      text: i18n.t('sourceType'),
      value: 'sourceType',
      align: 'center',
      format: (value) => IntegrationSourceType.forInt(value)?.name
    },
    {
      sortable: true,
      text: i18n.t('debitCode'),
      value: 'debitAccountCode',
      align: 'center'
    },
    {
      sortable: true,
      text: i18n.t('creditCode'),
      value: 'creditAccountCode',
      align: 'center'
    },
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activity',
      align: 'center'
    },
    {
      sortable: true,
      text: i18n.t('group'),
      value: 'groupingKey1',
      align: 'center',
      format: valueOrNotAvailable
    },
    {
      sortable: true,
      text: i18n.t('status'),
      value: 'integrationStatus',
      align: 'center',
      format: (value) => IntegrationStatus.forInt(value)?.name
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'absAmount',
      align: 'right'
    }
  ]

  switch (category.value) {
    case AccountingCategory.Receivable.value:
      headers.splice(5, 0, ...[
        {
          sortable: true,
          text: i18n.t('productCode'),
          value: 'productCode',
          align: 'center'
        },
        {
          sortable: true,
          text: i18n.t('ext1'),
          value: 'ext1',
          align: 'center',
          format: valueOrNotAvailable
        },
        {
          sortable: true,
          text: i18n.t('ext2'),
          value: 'ext2',
          align: 'center',
          format: valueOrNotAvailable
        }
      ])

      // remove grouping key as it is not applicable for receivables
      headers = headers.filter(h => h.value !== 'groupingKey1')
      break
    case AccountingCategory.Accrual.value:
      headers.splice(5, 0, ...[
        {
          sortable: true,
          text: i18n.t('counterpartyExportCode'),
          value: 'counterpartyExportCode',
          align: 'center',
          format: valueOrNotAvailable
        }
      ])
      break
    default: break
  }

  return headers
}
